import { usePerfChartSymbols } from '@/composables/usePerfChartSymbols';
import { isSharpCharts2DateStringOlderThanMinimumAllowed } from '@/composables/isSharpCharts2DateStringOlderThanMinimumAllowed';
import { getDefaultSharpCharts2RangeStartDate } from '@/utils/getDefaultSharpCharts2RangeStartDate';
import { isValidSharpCharts2DateString } from '@/utils/isValidSharpCharts2DateString';
import { getFormattedTodayDate } from '@/utils/getFormattedTodayDate';
import { getFormattedYearToDateStartDate } from '@/utils/getFormattedYearToDateStartDate';
import { PERIOD_OPTIONS_ARRAY } from '@/enums/period-options';
import { RANGE_OPTION_FILL_THE_CHART, RANGE_OPTION_PREDEFINED_RANGE, RANGE_OPTION_YEAR_TO_DATE, RANGE_OPTION_SELECT_START_END, RANGE_OPTIONS_ARRAY } from '@/enums/range-options';
import { RangeYearsOption, RANGE_YEARS_OPTIONS_ARRAY } from '@/enums/range-years-options';
import { RangeMonthsOption, RANGE_MONTHS_OPTIONS_ARRAY } from '@/enums/range-months-options';
import { RangeDaysOption, RANGE_DAYS_OPTIONS_ARRAY } from '@/enums/range-days-options';
import { DEFAULT_RANGE_BAR_OPTION, RANGE_BAR_OPTIONS_ARRAY, isRangeBarOption } from '@/enums/range-bar-options';
import { DEFAULT_RANGE_GAP_OPTION, RANGE_GAP_OPTIONS_ARRAY, isRangeGapOption } from '@/enums/range-gap-options';
import { MIN_ALLOWED_DATE_STRING } from '@/enums/min-allowed-date-string';
import { CHART_TYPE_OPTION_PERF_CHART } from '@/enums/chart-type-options';
import { ChartStyle } from '@/interfaces/chart';

export function getStyleCacheSettingsOverrides(styleUrlString: string): Partial<ChartStyle> {
  const { MAXIMUM_NUMBER_OF_PERF_CHART_SYMBOLS, getPerfChartSymbols } = usePerfChartSymbols();
  // this code needs to match the logic within the adjustTags function in
  // sharpcharts-servlet/src/main/java/com/stockcharts/sharpcharts2/servlet/ChartServlet.java
  // as closely as possible so that the SC3 Workbench code can accurately interpret the chart
  // that will be generated by any given "style" url string

  const urlSearchParams = new URLSearchParams(styleUrlString.toLowerCase().replace(/\s/g, ''));

  const period = urlSearchParams.get('p') ?? urlSearchParams.get('period') ?? urlSearchParams.get('barwidth') ?? '';
  const bar = urlSearchParams.get('b') ?? urlSearchParams.get('bar') ?? '';
  const gap = urlSearchParams.get('g') ?? urlSearchParams.get('gap') ?? '';
  const years = urlSearchParams.get('yr') ?? urlSearchParams.get('years') ?? '';
  const months = urlSearchParams.get('mn') ?? urlSearchParams.get('months') ?? '';
  const days = urlSearchParams.get('dy') ?? urlSearchParams.get('days') ?? urlSearchParams.get('ndays') ?? '';
  let startDate = urlSearchParams.get('st') ?? urlSearchParams.get('start') ?? '';
  let endDate = urlSearchParams.get('en') ?? urlSearchParams.get('end') ?? '';
  const isPerfChartChartType = typeof urlSearchParams.get('compare') === 'string';
  const perfChartSymbolsString = urlSearchParams.get('compare') ?? '';
  const useMainChartSymbolAsBaseline = urlSearchParams.get('perf');

  let chartSettingsOverrides: Partial<ChartStyle> = {};

  const periodOption = PERIOD_OPTIONS_ARRAY.find(p => p.sharpCharts2Id.toLowerCase() === period) ?? null;

  if (periodOption) {
    chartSettingsOverrides.period = periodOption.value;
  }

  const rangeYears = RANGE_YEARS_OPTIONS_ARRAY.find(y => y.toLowerCase() === years) ?? RangeYearsOption.Zero;
  const rangeMonths = RANGE_MONTHS_OPTIONS_ARRAY.find(m => m.toLowerCase() === months) ?? RangeMonthsOption.Zero;
  const rangeDays = RANGE_DAYS_OPTIONS_ARRAY.find(d => d.toLowerCase() === days) ?? RangeDaysOption.Zero;
  const numDays = Number(rangeDays) + (30 * Number(rangeMonths)) + (365 * Number(rangeYears));

  if (numDays > 0) {
    const rangeOption = RANGE_OPTIONS_ARRAY.find(range =>
      range.useNumberOfDays &&
      range !== RANGE_OPTION_PREDEFINED_RANGE &&
      range.numberOfDays === rangeDays &&
      range.numberOfMonths === rangeMonths &&
      range.numberOfYears === rangeYears
    ) ?? RANGE_OPTION_PREDEFINED_RANGE;

    chartSettingsOverrides = {
      ...chartSettingsOverrides,
      rangeStartDate: getDefaultSharpCharts2RangeStartDate(),
      rangeEndDate: '',
      range: rangeOption.title,
      rangeBar: rangeOption.barValue,
      rangeGap: rangeOption.gapValue,
      rangeDays: rangeDays,
      rangeMonths: rangeMonths,
      rangeYears: rangeYears
    };
  }

  if (startDate.toLowerCase().includes('today')) {
    startDate = '';
  }

  if (endDate.toLowerCase().includes('today')) {
    endDate = '';
  }

  if (isValidSharpCharts2DateString(startDate) && (startDate || endDate)) {
    let rangeStartDate = isSharpCharts2DateStringOlderThanMinimumAllowed(startDate) ? MIN_ALLOWED_DATE_STRING : startDate;
    let rangeEndDate = '';

    if (isValidSharpCharts2DateString(endDate)) {
      rangeEndDate = isSharpCharts2DateStringOlderThanMinimumAllowed(endDate) ? MIN_ALLOWED_DATE_STRING : endDate;
    }

    const today = getFormattedTodayDate();

    if (rangeStartDate === today) {
      rangeStartDate = '';
    }

    if (rangeEndDate === today) {
      rangeEndDate = '';
    }

    const rangeOption = rangeStartDate === getFormattedYearToDateStartDate() && rangeEndDate === '' ? RANGE_OPTION_YEAR_TO_DATE : RANGE_OPTION_SELECT_START_END;

    chartSettingsOverrides = {
      ...chartSettingsOverrides,
      rangeStartDate: rangeStartDate,
      rangeEndDate: rangeEndDate,
      range: rangeOption.title,
      rangeBar: rangeOption.barValue,
      rangeGap: rangeOption.gapValue,
      rangeDays: rangeOption.numberOfDays,
      rangeMonths: rangeOption.numberOfMonths,
      rangeYears: rangeOption.numberOfYears
    };
  }

  const rangeBar = RANGE_BAR_OPTIONS_ARRAY.find(b => b.toLowerCase() === bar) ?? null;
  const rangeGap = RANGE_GAP_OPTIONS_ARRAY.find(g => g.toLowerCase() === gap) ?? null;

  if (isRangeBarOption(rangeBar) && isRangeGapOption(rangeGap)) {
    chartSettingsOverrides = {
      ...chartSettingsOverrides,
      rangeStartDate: getDefaultSharpCharts2RangeStartDate(),
      rangeEndDate: '',
      range: RANGE_OPTION_FILL_THE_CHART.title,
      rangeBar: rangeBar,
      rangeGap: rangeGap,
      rangeDays: RANGE_OPTION_FILL_THE_CHART.numberOfDays,
      rangeMonths: RANGE_OPTION_FILL_THE_CHART.numberOfMonths,
      rangeYears: RANGE_OPTION_FILL_THE_CHART.numberOfYears
    };
  } else if (isRangeBarOption(rangeBar)) {
    chartSettingsOverrides = {
      ...chartSettingsOverrides,
      rangeStartDate: getDefaultSharpCharts2RangeStartDate(),
      rangeEndDate: '',
      range: RANGE_OPTION_FILL_THE_CHART.title,
      rangeBar: rangeBar,
      rangeGap: DEFAULT_RANGE_GAP_OPTION,
      rangeDays: RANGE_OPTION_FILL_THE_CHART.numberOfDays,
      rangeMonths: RANGE_OPTION_FILL_THE_CHART.numberOfMonths,
      rangeYears: RANGE_OPTION_FILL_THE_CHART.numberOfYears
    };
  } else if (isRangeGapOption(rangeGap)) {
    chartSettingsOverrides = {
      ...chartSettingsOverrides,
      rangeStartDate: getDefaultSharpCharts2RangeStartDate(),
      rangeEndDate: '',
      range: RANGE_OPTION_FILL_THE_CHART.title,
      rangeBar: DEFAULT_RANGE_BAR_OPTION,
      rangeGap: rangeGap,
      rangeDays: RANGE_OPTION_FILL_THE_CHART.numberOfDays,
      rangeMonths: RANGE_OPTION_FILL_THE_CHART.numberOfMonths,
      rangeYears: RANGE_OPTION_FILL_THE_CHART.numberOfYears
    };
  }

  if (isPerfChartChartType) {
    chartSettingsOverrides = {
      ...chartSettingsOverrides,
      chartType: CHART_TYPE_OPTION_PERF_CHART.value,
      perfChartSymbols: getPerfChartSymbols(perfChartSymbolsString).slice(0, MAXIMUM_NUMBER_OF_PERF_CHART_SYMBOLS)
    };
  }

  if (useMainChartSymbolAsBaseline === 'true') {
    chartSettingsOverrides = {
      ...chartSettingsOverrides,
      useMainChartSymbolAsBaseline: true
    };
  } else if (useMainChartSymbolAsBaseline === '' || useMainChartSymbolAsBaseline === 'false') {
    chartSettingsOverrides = {
      ...chartSettingsOverrides,
      useMainChartSymbolAsBaseline: false
    };
  }

  return chartSettingsOverrides;
}
